import {
  ShoppingBag, Search, Box, ShieldCheck, Printer, Download,
  Upload, Sparkles, Wallet, TrendingUp, Banknote, Image as ImageIcon,
  ClipboardList, HandCoins, MessageCircle, Truck, IndianRupee,
} from "lucide-react";

/** Slide content for the three first-login tours. Each slide has:
 *  - icon
 *  - title
 *  - bullets: 2-3 short lines
 *  - accent: color hex for the visual card
 *  - dashRoute (optional): if the "Try it" button opens a specific tab
 *
 *  Kept in data-only form so the same UI component renders every role. */

export const BUYER_TOUR = {
  role: "buyer",
  title: "Welcome to Jeweltix",
  subtitle: "Here's how you buy production-ready CAD in under a minute.",
  cta: "Start browsing",
  ctaRoute: "/marketplace",
  slides: [
    {
      icon: Search,
      accent: "#2563EB",
      title: "1 · Browse the marketplace",
      bullets: [
        "Filter by category, format (.3dm / .stl) or price",
        "Every listing shows real weight for casting (18K, silver, platinum)",
        "Hover a card for a live 3D preview — no downloads needed",
      ],
    },
    {
      icon: Box,
      accent: "#7C3AED",
      title: "2 · Preview in true 3D",
      bullets: [
        "Rotate, zoom, and switch between shaded / wireframe modes",
        "Confirm the design is production-ready before you pay",
        "Visual search: snap a photo and find similar designs instantly",
      ],
    },
    {
      icon: ShieldCheck,
      accent: "#10B981",
      title: "3 · Pay via Razorpay escrow",
      bullets: [
        "UPI · Cards · Netbanking · Wallets — all supported",
        "Funds are held in escrow for 48 hours",
        "Raise a dispute if the file is corrupt — our team reviews and refunds if valid",
      ],
    },
    {
      icon: Download,
      accent: "#F59E0B",
      title: "4 · Instant download",
      bullets: [
        "Get the .3dm + .stl files the moment payment clears",
        "Every download is watermarked with a hidden fingerprint",
        "Access purchases anytime from your Buyer Dashboard",
      ],
    },
    {
      icon: Printer,
      accent: "#EC4899",
      title: "5 · Print to Reality (optional)",
      bullets: [
        "Send any purchased file to a CAM Vendor near you",
        "Vendors quote a real print price after seeing the file",
        "Chat in-app, pay only when you confirm the quote",
      ],
    },
  ],
};

export const SELLER_TOUR = {
  role: "seller",
  title: "Welcome to Jeweltix, Designer",
  subtitle: "Everything you need to know before you upload your first design.",
  cta: "Upload your first design",
  ctaRoute: "/seller?tab=upload",
  slides: [
    {
      icon: Upload,
      accent: "#2563EB",
      title: "1 · Upload your CAD",
      bullets: [
        ".3dm (editable) or .stl (print-ready) — or both for the same design",
        "Add title, category, CAM weight, and a price in ₹",
        "One design at a time via Upload, or many at once via Bulk Upload",
      ],
    },
    {
      icon: Sparkles,
      accent: "#7C3AED",
      title: "2 · Let AI write the description",
      bullets: [
        "Click the ✨ Describe button — GPT / Gemini drafts marketplace copy",
        "Auto-computed weights across 18K, 22K, silver, platinum",
        "Auto-rendered 360° preview + 5 angle photos — no work from you",
      ],
    },
    {
      icon: ImageIcon,
      accent: "#059669",
      title: "3 · Upload your own renders — sell more",
      bullets: [
        "Jeweltix auto-generates 8 matte studio photos for every design",
        "But YOUR renders show first + become the marketplace-card hero — sellers who upload their own images see more clicks",
        "Just JPG, PNG or WebP · up to 10 images · square + 1200 × 1200 recommended",
      ],
    },
    {
      icon: ShieldCheck,
      accent: "#F59E0B",
      title: "4 · Ownership warranty",
      bullets: [
        "Tick the amber warranty before submitting — required by law",
        "Uploading someone else's design = permanent termination",
        "Every listing is reviewed by the Jeweltix team within 24 hours",
      ],
    },
    {
      icon: Wallet,
      accent: "#10B981",
      title: "5 · Get paid",
      bullets: [
        "Overview shows Gross → Platform Fee → Gateway Fee → Net",
        "Filter earnings by Today / Week / Month / Quarter / Year",
        "Top-selling design card shows which file is bringing in revenue",
      ],
    },
    {
      icon: Banknote,
      accent: "#EC4899",
      title: "6 · Payouts on demand",
      bullets: [
        "48-hour escrow hold on every sale — release automatic after",
        "Complete KYC + link a bank/UPI to withdraw",
        "Request payout anytime from the Payouts tab",
      ],
    },
  ],
};

export const CAM_VENDOR_TOUR = {
  role: "cam_vendor",
  title: "Welcome, CAM Vendor",
  subtitle: "How print jobs flow through your shop.",
  cta: "See your job queue",
  ctaRoute: "/vendor?tab=jobs",
  slides: [
    {
      icon: ClipboardList,
      accent: "#2563EB",
      title: "1 · Jobs arrive automatically",
      bullets: [
        "New print jobs appear in your queue — sorted by delivery mode",
        "See machine type, weight, deadline, and buyer city upfront",
        "Accept or decline within 24 hours — declined jobs re-route",
      ],
    },
    {
      icon: HandCoins,
      accent: "#7C3AED",
      title: "2 · Accept and quote",
      bullets: [
        "Accepting unlocks the .stl file (never the .3dm — designer IP)",
        "Enter your real printing cost — you set the price, not us",
        "Buyer sees the quote, pays via Razorpay to confirm",
      ],
    },
    {
      icon: MessageCircle,
      accent: "#F59E0B",
      title: "3 · Chat with the buyer",
      bullets: [
        "In-app chat per job — clarify metal, finish, deadline",
        "Buyer contact (name, mobile, address) unlocks on accept",
        "Use it only for this job — file & data are single-use",
      ],
    },
    {
      icon: Truck,
      accent: "#10B981",
      title: "4 · Print → Ship → Complete",
      bullets: [
        "Mark 'Printing' when you start, 'Ready' when the piece is done",
        "For delivery orders, mark 'Shipped' with a courier reference",
        "Buyer marks 'Received' → job auto-completes",
      ],
    },
    {
      icon: IndianRupee,
      accent: "#EC4899",
      title: "5 · Earnings",
      bullets: [
        "Jeweltix takes 10% platform margin + gateway fees",
        "48-hour escrow hold protects both sides",
        "Auto-payout to your linked account after the hold releases",
      ],
    },
  ],
};

export const TOUR_MAP = {
  buyer: BUYER_TOUR,
  seller: SELLER_TOUR,
  cam_vendor: CAM_VENDOR_TOUR,
};
